import { ListFilters, ListProps } from "@/types/list";
import { List } from "@/components/List";
import { ToggleGroup, ToggleGroupItem } from "../ui/toggle-group";
import { ListIcon, MapIcon } from "lucide-react";
import React, { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { Map, MapProps } from "@/components/Map";
import { ListToolbarActions } from "../List/ListToolbar/ListToolbarActions";
import { cx } from "class-variance-authority";
import { useSearchParams } from "react-router";
import { ColumnFilter, ColumnFiltersState } from "@tanstack/react-table";
import { useTableFilters } from "@/hooks/useTableFilters";

type View<TKey extends string = never> = "list" | "map" | TKey;

export interface ExtraView<TKey extends string = string> {
	key: TKey;
	icon: React.ReactNode;
	label: string;
	render: (filter: ListFilters) => React.ReactNode;
	onActivate?: () => void;
	className?: string;
}

export type MapFilters = ListFilters & {
	filters: MapFiltersState;
};

export type MapFilter = ColumnFilter;
export type MapFiltersState = ColumnFiltersState;

interface ListMapToggleProps {
	onViewChange: (view: string) => void;
	view: string;
	extraViews: ExtraView[];
}
const ListMapToggle: React.FC<ListMapToggleProps> = ({ onViewChange, view, extraViews }) => {
	return (
		<div className="flex gap-2 items-end">
			<ToggleGroup
				type="single"
				value={view}
				onValueChange={value => value && onViewChange(value)}
				size="sm"
				className="gap-0 rounded-md border overflow-hidden h-8"
			>
				<ToggleGroupItem
					value="list"
					aria-label="List view"
					className="rounded-none border-r text-muted-foreground data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
				>
					<ListIcon className="h-4 w-4" />
				</ToggleGroupItem>
				<ToggleGroupItem
					value="map"
					aria-label="Map view"
					className="rounded-none text-muted-foreground data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
				>
					<MapIcon className="h-4 w-4" />
				</ToggleGroupItem>
				{extraViews.map(extraView => (
					<ToggleGroupItem
						key={extraView.key}
						value={extraView.key}
						aria-label={extraView.label}
						className="rounded-none border-l text-muted-foreground data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
					>
						{extraView.icon}
					</ToggleGroupItem>
				))}
			</ToggleGroup>
		</div>
	);
};

interface ListMapViewProps<TData, TKey extends string = never> {
	list: ListProps<TData>;
	map: MapProps<TData>;
	className?: string;
	filters?: (filter: ListFilters, view: View<TKey>) => React.ReactNode;
	initFromUrl?: boolean;
	enableUrlParams?: boolean;
	initialView?: View<TKey>;
	extraViews?: ExtraView<TKey>[];
}

export const ListMapView = observer(
	<TData, TKey extends string = never>({
		list,
		map,
		filters,
		className = "",
		initFromUrl = false,
		enableUrlParams = false,
		initialView = "list",
		extraViews = [],
	}: ListMapViewProps<TData, TKey>) => {
		const [searchParams, setSearchParams] = useSearchParams();

		const validViewKeys = ["map", ...extraViews.map(extraView => extraView.key)];

		const [view, setView] = useState<View<TKey>>(() => {
			if (initFromUrl) {
				const searchParamView = searchParams.get("view");
				return searchParamView && validViewKeys.includes(searchParamView)
					? (searchParamView as View<TKey>)
					: "list";
			} else {
				return initialView;
			}
		});
		const [mapMounted, setMapMounted] = useState(view === "map");

		useEffect(() => {
			if (enableUrlParams) {
				const searchParams = new URLSearchParams(window.location.search);
				if (view === "list") {
					searchParams.delete("view");
				} else {
					searchParams.set("view", view);
				}
				setSearchParams(searchParams, { replace: true });
			}
		}, [view, setSearchParams, enableUrlParams]);

		const tableFilters = useTableFilters(list.table);

		const mapFilters: ListFilters = {
			getFilter: id => map.mapStore.filters.getFilter(id),
			setFilter: (id, value) => map.mapStore.filters.setFilter(id, value),
			clearFilter: id => map.mapStore.filters.clear(id),
		};

		const activeFilter = view === "map" ? mapFilters : tableFilters;

		const activeExtraView = extraViews.find(extraView => extraView.key === view);

		const onViewChange = (nextView: string) => {
			setView(nextView as View<TKey>);
			if (nextView === "map" && !mapMounted) {
				setMapMounted(true);
			}
			if (nextView === "map" && map.mapStore.onFetch) {
				const filters = list.table.getState().columnFilters;
				map.mapStore.filters.setFilters(filters);
				map.mapStore.onFetch();
			}

			if (nextView === "list") {
				list.table.setColumnFilters(map.mapStore.filters.toJS);
			}

			extraViews.find(extraView => extraView.key === nextView)?.onActivate?.();
		};

		const filtersNode = filters?.(activeFilter, view);

		return (
			<div className={cx("flex flex-col flex-1 h-full min-h-0", className)}>
				<div className="flex flex-wrap md:flex-nowrap items-end py-4 gap-2 md:gap-4">
					<ListMapToggle view={view} onViewChange={onViewChange} extraViews={extraViews} />

					{filtersNode}

					{view === "list" && (
						<ListToolbarActions
							table={list.table}
							tableName={list.tableName}
							loading={list.layout.loading}
							expandedRowPanelExport={list.toolbar?.expandedRowPanelExport}
							refreshFunction={list.toolbar?.refreshFunction}
							actionCoppyUrl={list.toolbar?.actionCoppyUrl}
						/>
					)}
				</div>
				{view === "list" && <List {...list} toolbar={{ hideToolbar: true }} />}
				{mapMounted && (
					<div
						className={
							view === "map"
								? "flex-1 min-h-[500px] flex mb-6 rounded-md relative overflow-hidden"
								: "hidden"
						}
					>
						<Map {...map} />
					</div>
				)}
				{activeExtraView && (
					<div
						className={cx(
							"flex-1 min-h-[500px] flex mb-6 rounded-md relative overflow-hidden",
							activeExtraView.className,
						)}
					>
						{activeExtraView.render(activeFilter)}
					</div>
				)}
			</div>
		);
	},
);
